import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap, map, switchMap } from 'rxjs';
import { ROL_NAME, RolId } from '../permissions/permissions';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { CsrfService } from '../csrf/csrf.service';

export interface User {
  id: number;
  email: string;
  idRol: number;
  rol?: string;
  idIncidencia?: number;
  contingencia?: boolean | number;
}

export interface AuthState {
  user: User | null;
  loginType: 'email' | 'pin' | null;
}

export interface ContingenciaData {
  requiereContingencia: true;
  idUsuario: number;
  idRol: number;
  idProyecto: number;
  telefono: string;
  mensaje: string;
}

export interface Tramo {
  id: number;
  nombre: string;
}

export interface Linea {
  id: number;
  nombre: string;
  tramos: Tramo[];
}

interface TramoRaw {
  idTramo: number;
  tramo: string;
}

interface LineaRaw {
  id: number;
  title: string;
  tramos: TramoRaw[];
}

interface EstacionRaw {
  id: number;
  title: string;
}

export interface Estacion {
  id: number;
  nombre: string;
}

export type LoginResult = User | ContingenciaData;

export function isContingencia(result: LoginResult): result is ContingenciaData {
  return 'requiereContingencia' in result;
}

export function getRolName(idRol: number): string {
  return ROL_NAME[idRol as RolId] || 'UNKNOWN';
}

@Injectable({ providedIn: 'root' })
export class Auth {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private csrf = inject(CsrfService);

  private apiUrl = environment.apiUrl;

  private _user = signal<User | null>(null);
  user = this._user.asReadonly();

  private _loginType = signal<'email' | 'pin' | null>(null);
  loginType = this._loginType.asReadonly();

  get isAuthenticated(): boolean {
    return this._user() !== null;
  }

  get userRole(): number | undefined {
    return this._user()?.idRol;
  }

  get isPinLogin(): boolean {
    return this._loginType() === 'pin';
  }

  login(email: string, password: string) {
    return this.csrf.fetchToken().pipe(
      switchMap(() =>
        this.http.post<{ data: LoginResult }>(
          `${this.apiUrl}/auth/login`,
          { email, password },
          { withCredentials: true },
        ),
      ),
      map((res): LoginResult => {
        if (isContingencia(res.data)) return res.data;
        const u = res.data as User;
        return { ...u, rol: getRolName(u.idRol) };
      }),
      tap((result) => {
        if (!isContingencia(result)) {
          this._user.set(result);
          this._loginType.set('email');
          this.persistUser(result);
        }
      }),
    );
  }

  sesionContingencia(body: {
    idUsuario: number;
    idRol: number;
    idProyecto: number;
    idEstacion: number;
    idTramo: number;
    telefono: string;
  }) {
    return this.csrf.fetchToken().pipe(
      switchMap(() =>
        this.http.post<{ data: User }>(
          `${this.apiUrl}/auth/sesion-contingencia`,
          body,
          { withCredentials: true },
        ),
      ),
      map((res) => ({ ...res.data, rol: getRolName(res.data.idRol) })),
      tap((user) => {
        this._user.set(user);
        this._loginType.set('email');
        this.persistUser(user);
      }),
    );
  }

  getCatalogoLineas(idProyecto: number) {
    return this.http
      .get<{ data: Array<{ lineas: LineaRaw[] }> }>(`${this.apiUrl}/auth/catalogo/lineas`, {
        params: { idProyecto: idProyecto.toString() },
        withCredentials: true,
      })
      .pipe(
        map((res) =>
          (res.data[0]?.lineas ?? []).map((l): Linea => ({
            id: l.id,
            nombre: l.title,
            tramos: l.tramos.map((t): Tramo => ({ id: t.idTramo, nombre: t.tramo })),
          })),
        ),
      );
  }

  getCatalogoEstaciones(idProyecto: number, idLinea: number) {
    return this.http
      .get<{ data: Array<{ estaciones: EstacionRaw[] }> }>(`${this.apiUrl}/auth/catalogo/estaciones`, {
        params: { idProyecto: idProyecto.toString(), idLinea: idLinea.toString() },
        withCredentials: true,
      })
      .pipe(
        map((res) =>
          (res.data[0]?.estaciones ?? []).map((e): Estacion => ({ id: e.id, nombre: e.title })),
        ),
      );
  }

  loginPin(telefono: string, pinIncidencia: string) {
    return this.csrf.fetchToken().pipe(
      switchMap(() =>
        this.http.post<{ data: User }>(
          `${this.apiUrl}/auth/login`,
          { telefono, pinIncidencia },
          { withCredentials: true },
        ),
      ),
      map((res) => ({ ...res.data, rol: getRolName(res.data.idRol) })),
      tap((user) => {
        this._user.set(user);
        this._loginType.set('pin');
        this.persistUser(user);
      }),
    );
  }

  me() {
    return this.http
      .get<{ data: User }>(`${this.apiUrl}/auth/me`, {
        withCredentials: true,
      })
      .pipe(
        map((res) => ({ ...res.data, rol: getRolName(res.data.idRol) })),
        tap((user) => {
          this._user.set(user);
          this.persistUser(user);
        }),
      );
  }

  logout() {
    return this.http
      .post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this._user.set(null);
          this._loginType.set(null);
          this.clearStorage();
          this.csrf.clear();
        }),
      );
  }

  private persistUser(user: User) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('user', JSON.stringify(user));
      const loginType = this._loginType();
      if (loginType) {
        localStorage.setItem('loginType', loginType);
      }
    }
  }

  private clearStorage() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('user');
      localStorage.removeItem('loginType');
    }
  }

  clearSessionLocal() {
    this._user.set(null);
    this._loginType.set(null);
    this.clearStorage();
  }

  loadFromStorage(): User | null {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const user = JSON.parse(stored) as User;
          this._user.set(user);
          
          const loginType = localStorage.getItem('loginType') as 'email' | 'pin' | null;
          this._loginType.set(loginType);
          
          return user;
        } catch {
          this.clearStorage();
        }
      }
    }
    return null;
  }
}